import { splitPrdSections, requirementRefs } from '#/lib/prd-sections'
import { slugify } from '#/lib/slug'
import type { AcceptanceCriterion } from '#/lib/types'

export interface ExportFeature { id: string; name: string; summary: string | null; phase: number; position: number; spec: string | null }
export interface ExportSub { parentId: string | null; name: string; summary: string | null }
export interface ExportTask {
  id: string
  featureId: string
  title: string
  description: string
  layer: string
  priority: string
  status: string
  ref: string
  dependsOn?: string[] | null
  parallelSafe?: boolean | null
  // ---- Proof-of-Done (moat plan §2.1) — present on every task since the acceptance migration
  acceptance?: AcceptanceCriterion[] | null
  tier?: string | null
  ownedFiles?: string[] | null
  verification?: string | null
  // ---- non-tech F0: plain-language labels (only set for plan.summary_mode = 'simple')
  plainTitle?: string | null
  plainDone?: string | null
}
export interface ExportInput { title: string; language: string; prd: string; idea?: string; features: ExportFeature[]; subs: ExportSub[]; tasks: ExportTask[] }

export const slugOf = (title: string) => slugify(title) || 'plan'

/** Bullets of a feature spec's "Done when / Selesai bila" section (acceptance criteria). */
export function specDoneBullets(spec: string | null): string[] {
  if (!spec) return []
  const parts = spec.split(/^## /m)
  const done = parts.find((p) => /^(done when|selesai bila)/i.test(p.trim()))
  if (!done) return []
  return done.split('\n').slice(1).map((l) => l.trim()).filter((l) => /^[-*•]\s+/.test(l)).map((l) => l.replace(/^[-*•]\s+/, ''))
}

/** Goal paragraph of a feature spec ("Goal / Tujuan"). */
export function specGoal(spec: string | null): string {
  if (!spec) return ''
  const parts = spec.split(/^## /m)
  const goal = parts.find((p) => /^(goal|tujuan)/i.test(p.trim()))
  return goal ? goal.split('\n').slice(1).join('\n').trim() : ''
}

/** PRD §3 sub-bullets (acceptance criteria + depends-on) for a feature name. */
export function prdFeatureBlock(prd: string, name: string): { criteria: string[]; dependsOn: string[] } {
  const s3 = splitPrdSections(prd).get('3')?.body ?? ''
  const lines = s3.split('\n')
  const norm = (x: string) => x.toLowerCase().replace(/\[(must|should|could)\]/gi, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
  const criteria: string[] = []
  const dependsOn: string[] = []
  let inside = false
  for (const line of lines) {
    const top = /^[-*]\s+\*\*(.+?)\*\*/.exec(line)
    if (top) { inside = norm(top[1]) === norm(name); continue }
    if (/^###\s/.test(line)) { inside = false; continue }
    if (!inside) continue
    const sub = /^\s+[-*]\s+(.+)$/.exec(line)
    if (!sub) continue
    const text = sub[1].trim()
    const dep = /^(depends on|bergantung pada)\s*:\s*(.+)$/i.exec(text)
    if (dep) dependsOn.push(...dep[2].split(/,|;/).map((x) => x.trim()).filter(Boolean))
    else criteria.push(text)
  }
  return { criteria, dependsOn }
}

export function sectionBody(prd: string, n: string): string {
  return splitPrdSections(prd).get(n)?.body ?? ''
}

export function sectionTitle(prd: string, n: string, fallback: string): string {
  return splitPrdSections(prd).get(n)?.title ?? fallback
}

export function refsIn(text: string): string[] {
  return requirementRefs(text)
}

export const byPhaseThenPosition = (a: ExportFeature, b: ExportFeature) => a.phase - b.phase || a.position - b.position

// ---------------------------------------------------------------- format hub (moat plan §4.3)

/** Every `?format=` the export route understands, in the order the UI shows them. */
export const EXPORT_FORMATS = [
  { id: 'md', label: 'PRD (Markdown)', ext: 'md' },
  { id: 'zip', label: 'Bystep bundle', ext: 'zip' },
  { id: 'kiro', label: 'Kiro', ext: 'zip' },
  { id: 'speckit', label: 'GitHub Spec Kit', ext: 'zip' },
  { id: 'openspec', label: 'OpenSpec', ext: 'zip' },
  { id: 'backlog', label: 'Backlog.md', ext: 'zip' },
  { id: 'taskmaster', label: 'Task Master', ext: 'zip' },
  { id: 'claude-tasks', label: 'Claude Code Tasks', ext: 'zip' },
  { id: 'github-issues', label: 'GitHub Issues', ext: 'zip' },
  { id: 'linear', label: 'Linear', ext: 'zip' },
] as const

export type ExportFormat = (typeof EXPORT_FORMATS)[number]['id']
/** Formats that produce a ZIP bundle (everything but the raw PRD markdown). */
export const ZIP_FORMATS: readonly string[] = EXPORT_FORMATS.filter((f) => f.ext === 'zip').map((f) => f.id)

export const featureSlug = (f: Pick<ExportFeature, 'name'>) => slugify(f.name) || 'feature'

/** Tasks of a feature, in board order. */
export const tasksOf = (input: ExportInput, featureId: string) => input.tasks.filter((t) => t.featureId === featureId)

/**
 * Acceptance texts for a task: its own `acceptance[]` first, then the feature's
 * PRD §3 sub-bullets / spec "Done when" bullets as a fallback so no target format ships empty.
 */
export function acceptanceTexts(task: ExportTask, feature: ExportFeature | undefined, prd: string): string[] {
  const own = (task.acceptance ?? []).map((a) => a.text.trim()).filter(Boolean)
  if (own.length) return own
  if (!feature) return []
  const block = prdFeatureBlock(prd, feature.name)
  return block.criteria.length ? block.criteria : specDoneBullets(feature.spec)
}

/** Files a task owns: `ownedFiles[]`, else a "Files: a, b" line in the description. */
export function filesOf(task: ExportTask): string[] {
  const owned = (task.ownedFiles ?? []).filter(Boolean)
  if (owned.length) return owned
  const line = /^(?:files|file|berkas)\s*:\s*(.+)$/im.exec(task.description)?.[1]
  return line ? line.split(/[,;]/).map((x) => x.trim()).filter(Boolean) : []
}

/** A task counts as proven when Bystep recorded passing evidence (or an explicit human override). */
export const isVerified = (task: ExportTask) => task.verification === 'verified' || task.verification === 'override'

/** Description without the machine-ish "Files:" line, trimmed for issue/card bodies. */
export function plainDescription(task: ExportTask): string {
  return task.description
    .split('\n')
    .filter((l) => !/^\s*(?:files|file|berkas)\s*:/i.test(l))
    .join('\n')
    .trim()
}

/** Ready-made README block for every bundle: what the format is, and how Bystep mapped onto it. */
export function bundleReadme(opts: { title: string; tool: string; language: string; layout: string[]; mapping: [string, string][]; notes?: string[] }): string {
  const id = opts.language === 'id'
  const out = [
    `# ${opts.title} → ${opts.tool}`,
    '',
    id
      ? `Diekspor dari Bystep. Salin isi bundel ini ke akar repo Anda, lalu lanjutkan dengan ${opts.tool}.`
      : `Exported from Bystep. Copy the contents of this bundle into your repository root and carry on in ${opts.tool}.`,
    '',
    `## ${id ? 'Isi' : 'Layout'}`,
    '',
    ...opts.layout.map((l) => `- \`${l}\``),
    '',
    `## ${id ? 'Pemetaan' : 'Mapping'}`,
    '',
    `| Bystep | ${opts.tool} |`,
    '| --- | --- |',
    ...opts.mapping.map(([a, b]) => `| ${a} | ${b} |`),
    '',
  ]
  if (opts.notes?.length) out.push(`## ${id ? 'Catatan' : 'Notes'}`, '', ...opts.notes.map((n) => `- ${n}`), '')
  out.push(
    id
      ? '_Task yang bertanda **verified** sudah punya bukti lolos (Proof-of-Done) di Bystep._'
      : '_Tasks marked **verified** already carry passing evidence (Proof-of-Done) in Bystep._',
    '',
  )
  return out.join('\n')
}
